'use client';

import Image, { ImageProps } from 'next/image';
import { tv } from 'tailwind-variants';

import { useModal } from '@/hooks/use-modal.hook';
import { Gallery } from '@/components/ui/gallery.component';

export type ImageSize = '4xl' | 'xxxl' | 'xxl' | 'xl' | 'lg' | 'md' | 'sm';

export interface AvatarProps extends ImageProps {
  src: string;
  size?: ImageSize;
  alt: string;
  className?: string;
  height?: number;
  width?: number;
  shouldAddLightbox?: boolean;
  dataTestId?: string;
}

const avatarStyle = tv({
  slots: {
    image: 'rounded-lg border border-gray-300 bg-gray-100',
  },
  variants: {
    size: {
      '4xl': {
        image: 'rounded-lg max-w-36 max-h-36',
      },
      'xxxl': {
        image: 'rounded-lg max-w-32 max-h-32',
      },
      'xxl': {
        image: 'rounded-lg max-w-24 max-h-24',
      },
      'xl': {
        image: 'rounded-lg max-w-22 max-h-22',
      },
      'lg': {
        image: 'rounded-lg max-w-16 max-h-16',
      },
      'md': {
        image: 'rounded-lg max-w-12 max-h-12',
      },
      'sm': {
        image: 'rounded-lg max-w-8 max-h-8',
      },
    },
    shouldAddLightbox: {
      true: {
        image: 'cursor-pointer',
      },
    },
  },
});

function getSize(size: ImageSize): number {
  const sizes = {
    '4xl': 144,
    'xxxl': 128,
    'xxl': 96,
    'xl': 80,
    'lg': 64,
    'md': 48,
    'sm': 36,
  };
  return sizes[size] || sizes.md;
}

export function Avatar({
  src,
  size = 'md',
  alt,
  className,
  width,
  height,
  shouldAddLightbox = false,
  dataTestId,
  ...props
}: AvatarProps) {
  const { open, onOpenChange } = useModal();

  const { image } = avatarStyle({ size });

  const imageSize = getSize(size);

  if (!src) {
    return null;
  }

  return (
    <>
      <Image
        data-testid={dataTestId || 'avatar-image'}
        className={image({ className, shouldAddLightbox })}
        src={src}
        alt={alt}
        width={width || imageSize}
        height={height || imageSize}
        onClick={() => shouldAddLightbox && onOpenChange(true)}
        placeholder={size == 'sm' ? undefined : 'blur'}
        blurDataURL="iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN88/z5fwAJPAO7GgL9TAAAAABJRU5ErkJggg=="
        draggable={false}
        {...props}
      />

      {shouldAddLightbox && open && (
        <Gallery
          open={open}
          images={[{ id: '1', name: 'image', src: src }]}
          currentIndex={0}
          onOpenChange={onOpenChange}
        />
      )}
    </>
  );
}
